import { Router } from "@angular/router";

export type ApprovalProcessReturnTo = "page" | "modal";

export const APPROVAL_PROCESS_ROUTE = "approval-process";
export const APPROVAL_PROCESS_APPLICATION_ROUTE = "application";

export function normalizeApprovalProcessReturnTo(value?: string | null): ApprovalProcessReturnTo {
  return value === "page" ? "page" : "modal";
}

export function buildApprovalProcessApplicationRoute(applicationId: number | string): string[] {
  return ["/", APPROVAL_PROCESS_ROUTE, APPROVAL_PROCESS_APPLICATION_ROUTE, String(applicationId)];
}

export type ApprovalProcessNestedDialogCloseResult = {
  readonly status?: boolean;
  readonly application?: unknown;
  // Set on reject so the URL flow page can show a "rejected" feedback view instead of silently
  // redirecting to the dashboard (see closeApprovalProcessNestedDialogAfterReject below).
  readonly outcome?: "rejected";
};

/**
 * Page mode: close the nested MatDialog with success so the URL flow page reresolves entry.
 * Returns true when handled (caller should return early).
 */
export function closeApprovalProcessNestedDialogForPageReturn(
  dialogRef: { close: (result?: ApprovalProcessNestedDialogCloseResult) => void } | null | undefined,
  returnTo: ApprovalProcessReturnTo | string | undefined | null,
  application?: unknown
): boolean {
  if (normalizeApprovalProcessReturnTo(returnTo) !== "page" || !dialogRef) {
    return false;
  }

  dialogRef.close({ status: true, application });
  return true;
}

/**
 * Page mode: close nested MatDialog after reject so the URL flow page returns to dashboard.
 * Returns true when handled (caller should return early).
 */
export function closeApprovalProcessNestedDialogAfterReject(
  dialogRef: { close: (result?: ApprovalProcessNestedDialogCloseResult) => void } | null | undefined,
  returnTo: ApprovalProcessReturnTo | string | undefined | null
): boolean {
  if (normalizeApprovalProcessReturnTo(returnTo) !== "page" || !dialogRef) {
    return false;
  }

  dialogRef.close({ status: false, outcome: "rejected" });
  return true;
}

/**
 * Page mode reject exit: close nested dialog or navigate to dashboard when dialog ref is unavailable.
 * Returns true when page-mode navigation was handled.
 */
export function handleApprovalProcessPageRejectReturn(
  router: Router,
  dialogRef: { close: (result?: ApprovalProcessNestedDialogCloseResult) => void } | null | undefined,
  returnTo: ApprovalProcessReturnTo | string | undefined | null
): boolean {
  if (closeApprovalProcessNestedDialogAfterReject(dialogRef, returnTo)) {
    return true;
  }

  if (normalizeApprovalProcessReturnTo(returnTo) === "page") {
    navigateToApprovalProcessDashboard(router);
    return true;
  }

  return false;
}

export function navigateToApprovalProcessDashboard(router: Router): void {
  void router.navigate(["/", APPROVAL_PROCESS_ROUTE]);
}

/**
 * Page mode success exit: close nested MatDialog with status true so the URL flow page reresolves,
 * or navigate to the canonical application URL when no dialog ref is available.
 * Returns true when page-mode success navigation was handled.
 */
export function completeApprovalProcessPageFlowSuccess(
  router: Router,
  dialogRef: { close: (result?: ApprovalProcessNestedDialogCloseResult) => void } | null | undefined,
  returnTo: ApprovalProcessReturnTo | string | undefined | null,
  applicationId?: number | string | null,
  application?: unknown
): boolean {
  if (closeApprovalProcessNestedDialogForPageReturn(dialogRef, returnTo, application)) {
    return true;
  }

  if (normalizeApprovalProcessReturnTo(returnTo) === "page" && applicationId != null && applicationId !== "") {
    void router.navigate(buildApprovalProcessApplicationRoute(applicationId));
    return true;
  }

  return false;
}

/**
 * Redirect after nested approval flows (DocumentsValidation, AnalysisResults, etc.).
 * Modal: dashboard + reload (legacy). Page: canonical application URL without reload.
 */
export function redirectAfterApprovalFlow(
  router: Router,
  returnTo: ApprovalProcessReturnTo | string | undefined | null,
  applicationId?: number | string | null
): void {
  const mode = normalizeApprovalProcessReturnTo(returnTo ?? undefined);

  if (mode === "page" && applicationId != null && applicationId !== "") {
    void router.navigate(buildApprovalProcessApplicationRoute(applicationId));
    return;
  }

  void router.navigate(["/", APPROVAL_PROCESS_ROUTE]).then(() => window.location.reload());
}
